import React from 'react';
import { FiNavigation } from "react-icons/fi";
import { useGetWarehouseStockQuery } from 'Services/Admin/DashboardApiSlice';

interface WarehouseStockProps {
  warehouse: string;
  categoryId: string;
  productId: string;
}

export const WarehouseStock = ({ warehouse, categoryId, productId }: WarehouseStockProps) => {
  const plant = process.env.REACT_APP_PLANT || '';
  const { data: stockData, isLoading } = useGetWarehouseStockQuery({
    plant, warehouse, categoryId, productId
  }, { pollingInterval: 60000 });

  const warehouses = stockData?.results || [];
  
  // Define colors for variety
  const COLORS = ['#1E5EFF', '#2DD4BF', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4'];
  
  // Calculate a reasonable max for percentages based on the current visible data
  const maxVal = Math.max(...warehouses.map((w: any) => w.totalStock), 1000);
  const getPct = (val: number) => Math.min(100, (val / maxVal) * 100);

  // If there's only 1, 2 or 3 items, we'll center them vertically to fill the 400px card nicely
  const isMinimalData = warehouses.length > 0 && warehouses.length <= 3;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 xl:p-6 flex flex-col h-[400px] overflow-hidden relative">
      <style>{`
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .hide-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center gap-2 pb-4 border-b border-gray-100 mb-6 shrink-0">
        <div className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center text-teal-600 shadow-sm">
          <FiNavigation className="text-sm rotate-45" />
        </div>
        <h3 className="text-[16px] font-bold text-[#1B2559]">Warehouse Stock</h3>
      </div>
      
      {/* Content Area - Scrollable but with hidden scrollbar */}
      <div className={`flex-1 overflow-y-auto hide-scrollbar ${isMinimalData ? 'flex flex-col justify-center' : ''}`}>
        {isLoading ? (
           <div className="flex justify-center py-20">
             <div className="w-8 h-8 border-3 border-teal-500/20 border-t-teal-500 rounded-full animate-spin"></div>
           </div>
        ) : warehouses.length === 0 ? (
          <div className="text-center py-20 opacity-40">
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">No Inventory Records</p>
          </div>
        ) : (
          <div className={`${isMinimalData ? 'space-y-12' : 'space-y-8'} transition-all duration-500`}>
            {warehouses.map((w: any, idx: number) => (
              <div key={idx} className="space-y-3 px-1">
                <div className="flex justify-between items-start gap-3 text-[11px] font-bold tracking-tight">
                  {/* Removed truncate and max-width to show full warehouse text, allowed wrapping for long names */}
                  <span className="text-[#1B2559] uppercase opacity-90 flex-1 leading-[1.2] py-0.5 whitespace-normal break-words">
                    {w.warehouse}
                  </span>
                  <span className="text-blue-600 font-black tabular-nums shrink-0 whitespace-nowrap">
                    {w.totalStock.toLocaleString()} <span className="text-[9px] font-bold text-gray-400">ITEMS</span>
                  </span>
                </div>
                <div className="h-2.5 bg-gray-50 w-full rounded-full overflow-hidden border border-gray-100/30 relative">
                  <div 
                    className="h-full rounded-full transition-all duration-1000 shadow-sm" 
                    style={{ 
                      width: `${getPct(w.totalStock)}%`,
                      backgroundColor: COLORS[idx % COLORS.length]
                    }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
